import { useMemo, useState } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTitle } from 'react-use';

import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';

import { WebSession, useWebSessions } from './api';
import WebSessionDetailsDialog from './WebSessionDetailsDialog';
import WebSessionItem from './WebSessionItem';
import WebSessionItemSkeleton from './WebSessionItemSkeleton';

const Security = () => {
  const { t } = useTranslation();
  useTitle(t('pages.security'));
  useSegments(PageName.PERSONAL_SECURITY);

  const { sessions, status, refetch, revokeOne, revokeAll, isRevokingOne, isRevokingAll } =
    useWebSessions();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<WebSession | null>(null);

  const { current, others } = useMemo(() => {
    const grouped = { current: undefined as WebSession | undefined, others: [] as WebSession[] };
    for (const session of sessions) {
      if (session.is_current) {
        grouped.current = session;
      } else {
        grouped.others.push(session);
      }
    }
    return grouped;
  }, [sessions]);

  return (
    <SettingsContent className="mb-28 flex max-w-lg flex-col">
      {status === 'pending' && (
        <div className="flex w-full flex-col items-stretch gap-3">
          <WebSessionItemSkeleton />
          <WebSessionItemSkeleton />
          <WebSessionItemSkeleton />
        </div>
      )}

      {status === 'error' && (
        <Alert variant="destructive">
          <AlertTriangleIcon size={16} />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{t('web_sessions.load_error')}</span>
            <Button variant="outline-neutral" size="sm" onClick={() => refetch()}>
              {t('common.try_again')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {status === 'success' && (
        <div aria-live="polite">
          {!current && others.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-background px-4 py-12 text-center">
              <p className="text-base leading-none font-medium text-foreground">
                {t('web_sessions.empty_title')}
              </p>
              <p className="text-sm leading-5 font-normal text-muted-foreground">
                {t('web_sessions.empty_description')}
              </p>
            </div>
          )}
          {current && (
            <div className="mb-12 flex flex-col gap-3 self-stretch">
              <h2 className="px-1 text-lg leading-none font-semibold">
                {t('web_sessions.current_session')}
              </h2>
              <WebSessionItem session={current} onOpen={setSelected} />
            </div>
          )}
          {others.length > 0 && (
            <div className="flex flex-col gap-3 self-stretch">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-lg leading-none font-semibold">
                  {t('web_sessions.other_sessions')}
                </h2>
                <Button
                  variant="outline-neutral"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isRevokingAll}
                >
                  {t('web_sessions.revoke_all')}
                </Button>
              </div>
              {others.map((session) => (
                <WebSessionItem key={session.id} session={session} onOpen={setSelected} />
              ))}
            </div>
          )}
        </div>
      )}

      <WebSessionDetailsDialog
        session={selected}
        onClose={() => setSelected(null)}
        onRevoke={revokeOne}
        isRevoking={isRevokingOne}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('web_sessions.revoke_all_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('web_sessions.revoke_all_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isRevokingAll}
              onClick={async () => {
                trackEvent(EventName.ClickRevokeAllWebSessions);
                try {
                  await revokeAll();
                  setConfirmOpen(false);
                } catch {
                  // mutation.onError already toasted; keep dialog open for retry
                }
              }}
            >
              {t('web_sessions.revoke_all')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsContent>
  );
};

export default Security;
